/**
 * Merchant Application Configuration Constants
 * Centralizes all hardcoded values, magic numbers, and configuration options
 */

// Pipeline Configuration
export const PIPELINE_CONFIG = {
  // Column dimensions
  MIN_COLUMN_WIDTH: 286,
  MAX_COLUMN_WIDTH: 400,
  DEFAULT_COLUMN_WIDTH: 320,
  COLUMN_GAP: 16,
  COLUMN_PADDING: 12,
  
  // Card dimensions
  CARD_MIN_HEIGHT: 120,
  CARD_PADDING: 16,
  CARD_GAP: 8,
  
  // Colors with semantic meaning
  COLUMN_COLORS: {
    lead: '#6B7280',           // Gray - Initial contact
    phone: '#3B82F6',          // Blue - Active communication
    offer: '#8B5CF6',          // Purple - Proposal stage
    underwriting: '#F59E0B',   // Amber - Review process
    documents: '#EF4444',      // Red - Action required
    approved: '#10B981',       // Green - Success
    onboarded: '#059669',      // Emerald - Onboarded to processing
    live: '#22C55E',           // Green-500 - Live and processing
    processing: '#0EA5E9',     // Sky-500 - Actively processing transactions
    failed_submission: '#9333EA', // Purple-600 - Failed to submit to PayArc
    declined: '#DC2626',       // Red-600 - Declined application
  },

  // Status display names
  STATUS_LABELS: {
    lead: 'Lead',
    phone: 'Phone Call',
    offer: 'Offer Sent',
    underwriting: 'Underwriting',
    documents: 'Documents',
    approved: 'Approved',
    onboarded: 'Onboarded',
    live: 'Live',
    processing: 'Processing',
    failed_submission: 'Failed Submission',
    declined: 'Declined',
  },

  // Default columns configuration
  DEFAULT_COLUMNS: [
    { id: 'lead', title: 'Leads', color: '#6B7280' },
    { id: 'phone', title: 'Phone', color: '#3B82F6' },
    { id: 'offer', title: 'Offer', color: '#8B5CF6' },
    { id: 'underwriting', title: 'Underwriting', color: '#F59E0B' },
    { id: 'documents', title: 'Documents', color: '#EF4444' },
    { id: 'approved', title: 'Approved', color: '#10B981' },
    { id: 'onboarded', title: 'Onboarded', color: '#059669' },
    { id: 'live', title: 'Live', color: '#22C55E' },
    { id: 'processing', title: 'Processing', color: '#0EA5E9' },
    { id: 'failed_submission', title: 'Failed Submission', color: '#9333EA' },
    { id: 'declined', title: 'Declined', color: '#DC2626' },
  ],
  
  // Animation and interaction
  DRAG_ANIMATION_DURATION: 200,
  HOVER_TRANSITION_DURATION: 150,
  COLUMN_RESIZE_DEBOUNCE: 300,
} as const;

// Application Form Configuration
export const FORM_CONFIG = {
  // Step configuration
  STEPS: {
    BUSINESS_INFO: 1,
    PROCESSING_HISTORY: 2,
    BENEFICIAL_OWNERS: 3,
    BANK_DETAILS: 4,
    DOCUMENTATION: 5,
  },
  
  // Validation rules
  VALIDATION: {
    BUSINESS_NAME_MIN_LENGTH: 2,
    BUSINESS_NAME_MAX_LENGTH: 100,
    TAX_ID_LENGTH: 9,
    ROUTING_NUMBER_LENGTH: 9,
    PHONE_MIN_LENGTH: 10,
    EMAIL_MAX_LENGTH: 254,
    ZIP_CODE_LENGTH: 5,
    MIN_OWNERSHIP_PERCENTAGE: 25,
    MAX_OWNERSHIP_PERCENTAGE: 100,
  },
  
  // File upload limits
  FILE_UPLOAD: {
    MAX_FILE_SIZE: 10 * 1024 * 1024, // 10MB
    ALLOWED_TYPES: [
      'image/jpeg',
      'image/png',
      'image/webp',
      'application/pdf',
      'text/plain',
    ],
    MAX_FILES_PER_TYPE: 5,
  },
} as const;

// Data Processing Configuration
export const DATA_CONFIG = {
  // Field mapping for data normalization
  BUSINESS_NAME_FIELDS: [
    'corporationInfo.companyName',
    'corporationInfo.businessName',
    'companyName',
    'businessName',
  ],
  
  DBA_FIELDS: [
    'corporationInfo.dbaName',
    'corporationInfo.dba',
    'dbaName',
    'dba',
  ],
  
  // Default values
  DEFAULTS: {
    CURRENCY: 'USD',
    COUNTRY: 'US',
    BUSINESS_TYPE: 'LLC',
    PROCESSING_VOLUME: 0,
    AVERAGE_TICKET: 0,
  },
  
  // Query limits
  QUERY_LIMITS: {
    APPLICATIONS_PER_PAGE: 50,
    COMMUNICATIONS_PER_PAGE: 20,
    DOCUMENTS_PER_APPLICATION: 20,
    BANKING_PARTNERS_LIMIT: 100,
  },
} as const;

// UI Configuration
export const UI_CONFIG = {
  // Breakpoints (Tailwind compatible)
  BREAKPOINTS: {
    SM: 640,
    MD: 768,
    LG: 1024,
    XL: 1280,
    '2XL': 1536,
  },
  
  // Icon sizes
  ICON_SIZES: {
    XS: 12,
    SM: 16,
    MD: 20,
    LG: 24,
    XL: 32,
  },
  
  // Z-index layers
  Z_INDEX: {
    DROPDOWN: 1000,
    MODAL: 1050,
    TOOLTIP: 1070,
    TOAST: 1080,
  },
  
  // Debounce delays
  DEBOUNCE: {
    SEARCH: 300,
    RESIZE: 150,
    SAVE: 500,
    API_RETRY: 1000,
  },
} as const;

// Business Logic Constants
export const BUSINESS_CONFIG = {
  // Pipeline automation
  AUTO_MOVE_DELAYS: {
    LEAD_TO_PHONE: 24 * 60 * 60 * 1000, // 24 hours
    PHONE_TO_OFFER: 3 * 24 * 60 * 60 * 1000, // 3 days
    OFFER_EXPIRY: 7 * 24 * 60 * 60 * 1000, // 7 days
  },
  
  // Risk assessment thresholds
  RISK_THRESHOLDS: {
    HIGH_VOLUME: 100000, // $100k monthly
    HIGH_TICKET: 5000,   // $5k average ticket
    HIGH_RISK_INDUSTRIES: [
      'adult',
      'gambling',
      'cryptocurrency',
      'cbd',
      'firearms',
    ],
  },
  
  // Notification settings
  NOTIFICATIONS: {
    EMAIL_TEMPLATES: {
      APPLICATION_RECEIVED: 'application-received',
      DOCUMENTS_REQUIRED: 'documents-required',
      UNDERWRITING_UPDATE: 'underwriting-update',
      APPROVED: 'approved',
      DECLINED: 'declined',
      BANKING_PARTNER_ASSIGNED: 'banking-partner-assigned',
    },
    
    SEND_DELAYS: {
      APPLICATION_RECEIVED: 5 * 60 * 1000, // 5 minutes
      FOLLOW_UP: 24 * 60 * 60 * 1000, // 24 hours
      REMINDER: 7 * 24 * 60 * 60 * 1000, // 7 days
    },
  },
} as const;

// Export all configurations as a single object for easy importing
export const MERCHANT_APPLICATION_CONFIG = {
  PIPELINE: PIPELINE_CONFIG,
  FORM: FORM_CONFIG,
  DATA: DATA_CONFIG,
  UI: UI_CONFIG,
  BUSINESS: BUSINESS_CONFIG,
} as const;

// Type exports for TypeScript support
export type PipelineStatus = keyof typeof PIPELINE_CONFIG.STATUS_LABELS;
export type FormStep = keyof typeof FORM_CONFIG.STEPS;
export type RiskLevel = 'low' | 'medium' | 'high';
export type NotificationTemplate = keyof typeof BUSINESS_CONFIG.NOTIFICATIONS.EMAIL_TEMPLATES;